/**
 * Fantasy Console 0 - Theme Context
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react'
import {
  type Theme,
  type ThemeColors,
  DARK_THEME,
  getThemeById,
  applyTheme,
} from '../utils/theme'
import { loadPreferences, savePreferences } from '../utils/storage'

interface ThemeContextType {
  theme: Theme
  colors: ThemeColors
  setThemeId: (id: string) => void
}

const ThemeContext = createContext<ThemeContextType | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const prefs = loadPreferences()
    const t = getThemeById(prefs.themeId ?? 'dark')
    // Apply synchronously during initial render to prevent flash
    applyTheme(t)
    return t
  })

  // Apply on subsequent theme changes
  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  const setThemeId = useCallback((id: string) => {
    const newTheme = getThemeById(id)
    setTheme(newTheme)
    savePreferences({ themeId: id })
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, colors: theme.colors, setThemeId }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
